'use client';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store';
import { addEmail, removeEmail, toggleEmailNoti, toggleEmailNotiDay, fetchUserInfo } from '../redux/slices/dataSlice';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface EmailSettings {
  emailNotifications: boolean;
  emailList: string[];
  notificationDays: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
}

export default function AccountEmail() {
  const { t } = useTranslation(['account', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.data.userInfo);
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    emailNotifications: false,
    emailList: [],
    notificationDays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    }
  });
  const [newEmail, setNewEmail] = useState('');
  const [isAddingEmail, setIsAddingEmail] = useState(false);

  useEffect(() => {
    // Load user info from server
    dispatch(fetchUserInfo());
  }, [dispatch]);

  useEffect(() => {
    // Update local state when userInfo changes
    if (userInfo) {
      setEmailSettings({
        emailNotifications: userInfo.notification || false,
        emailList: userInfo.emailList || [],
        notificationDays: {
          monday: userInfo.emailNotiDays?.Monday || false,
          tuesday: userInfo.emailNotiDays?.Tuesday || false,
          wednesday: userInfo.emailNotiDays?.Wednesday || false,
          thursday: userInfo.emailNotiDays?.Thursday || false,
          friday: userInfo.emailNotiDays?.Friday || false,
          saturday: userInfo.emailNotiDays?.Saturday || false,
          sunday: userInfo.emailNotiDays?.Sunday || false
        }
      });
    }
  }, [userInfo]);

  const saveSettings = (newSettings: EmailSettings) => {
    setEmailSettings(newSettings);
    // Note: Settings are now saved via Redux actions and server API
  };

  const toggleNotifications = async () => {
    try {
      await dispatch(toggleEmailNoti()).unwrap();
      const newSettings = {
        ...emailSettings,
        emailNotifications: !emailSettings.emailNotifications
      };
      saveSettings(newSettings);
    } catch (error) {
      logger.error('Failed to toggle email notifications:', error);
    }
  };

  const toggleDay = async (day: keyof typeof emailSettings.notificationDays) => {
    try {
      // Convert day to proper case for API (Monday, Tuesday, etc.) and use "day" format like InsectDetectionWebsite
      const properCaseDay = day.charAt(0).toUpperCase() + day.slice(1);
      const payload = { day: properCaseDay };
      await dispatch(toggleEmailNotiDay(payload)).unwrap();
      
      const newSettings = {
        ...emailSettings,
        notificationDays: {
          ...emailSettings.notificationDays,
          [day]: !emailSettings.notificationDays[day]
        }
      };
      saveSettings(newSettings);
    } catch (error) {
      logger.error('Failed to toggle notification day:', error);
      logger.error('Day:', day, 'Payload sent:', { day: day.charAt(0).toUpperCase() + day.slice(1) });
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;

    try {
      await dispatch(addEmail({ email: newEmail.trim() })).unwrap();
      const newSettings = {
        ...emailSettings,
        emailList: [...emailSettings.emailList, newEmail.trim()]
      };
      saveSettings(newSettings);
      setNewEmail('');
      setIsAddingEmail(false);
    } catch (error) {
      logger.error('Failed to add email:', error);
    }
  };

  const handleRemoveEmail = async (emailToRemove: string) => {
    try {
      await dispatch(removeEmail({ email: emailToRemove })).unwrap();
      const newSettings = {
        ...emailSettings,
        emailList: emailSettings.emailList.filter(email => email !== emailToRemove)
      };
      saveSettings(newSettings);
    } catch (error) {
      logger.error('Failed to remove email:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Notifications Toggle */}
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{t('account:emailNotifications')}</h3>
            <p className="text-sm text-gray-800">
              {t('account:receiveEmailAlerts')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer hover:opacity-80 transition-opacity">
            <input
              type="checkbox"
              checked={emailSettings.emailNotifications}
              onChange={toggleNotifications}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Email Management */}
      {emailSettings.emailNotifications && (
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('account:emailAddresses')}</h3>
          
          {/* Email List */}
          <div className="space-y-2 mb-4">
            {emailSettings.emailList.length === 0 ? (
              <p className="text-sm text-gray-800 italic">{t('account:noEmailAddresses')}</p>
            ) : (
              emailSettings.emailList.map((email, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                  <span className="text-sm text-gray-900">{email}</span>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
                  >
                    {t('account:remove')}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Add Email */}
          {isAddingEmail ? (
            <div className="flex space-x-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('account:enterEmailAddress')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleAddEmail}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 cursor-pointer"
              >
                {t('account:add')}
              </button>
              <button
                onClick={() => {
                  setIsAddingEmail(false);
                  setNewEmail('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded-md hover:bg-gray-400 cursor-pointer"
              >
                {t('common:cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAddingEmail(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 cursor-pointer"
            >
              {t('account:addEmailAddress')}
            </button>
          )}
        </div>
      )}

      {/* Notification Schedule */}
      {emailSettings.emailNotifications && (
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('account:notificationSchedule')}</h3>
          <p className="text-sm text-gray-900 mb-4">{t('account:selectDaysForNotifications')}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(emailSettings.notificationDays).map(([day, enabled]) => (
              <label key={day} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={() => toggleDay(day as keyof typeof emailSettings.notificationDays)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-900 capitalize">{t(`account:${day}`)}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}